import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { makeRequest } from 'Douleutaras/services/apiClient';
import { RootState } from 'Douleutaras/redux/store';
import { mapServicePricesToCatalogue } from 'Douleutaras/views/pro-profile/components/ServicesCatalogue/servicesPricingMapper';
import {
  SellerServicePriceDocument,
  ServicesCatalogueData
} from 'Douleutaras/views/pro-profile/components/ServicesCatalogue/types';

/**
 * The relationships the listing links its labels through — sideloaded (`include=...`) so a row's
 * category / sub1 / sub2 titles resolve from `included` rather than a second round-trip each.
 */
const SERVICE_PRICE_INCLUDES = ['category', 'sub1Service', 'sub2Detail'];

/**
 * The public, seller-scoped service-price listing endpoint (ticket #5598). The `{username}`
 * segment makes the read public — no JWT required — and specific to the pro being viewed, so
 * the catalogue renders on the public profile page and server-side (SSR/crawlable). It returns
 * the same prices for every visitor of a given pro, which is what the edge-cached route needs.
 * The pro is addressed by username (the profile slug), matching the public profile route.
 */
export const servicePriceEndpoint = (username: string): string =>
  `api/public/v3/seller/${username}/service-price/`;

interface GetSellerServicePricesArgs {
  /** The pro's username (the profile slug used across the public profile routes). */
  username: string;
}

/**
 * Fetches a pro's service-price listing (unpaginated — a pro lists a handful of prices, so they
 * all come back in one response), sideloading the category / sub1 / sub2 label resources, and
 * maps it into the display catalogue tree. Returns an empty catalogue when the pro has no priced
 * services (the section and tab then stay hidden).
 */
export const getSellerServicePrices = createAsyncThunk<ServicesCatalogueData, GetSellerServicePricesArgs, { state: RootState }>(
  'seller/servicePrices/get',
  async ({ username }, api) => {
    const response = await makeRequest(servicePriceEndpoint(username), {
      method: 'GET',
      includes: SERVICE_PRICE_INCLUDES
    })(api);

    const doc = response?.data as SellerServicePriceDocument | undefined;
    if (!doc || !Array.isArray(doc.data)) {
      // A failed/undefined response (e.g., rejected request) — surface as an error state.
      throw new Error('Malformed service-price response');
    }

    return mapServicePricesToCatalogue(doc);
  }
);

export interface ServicesPricingState {
  data?: ServicesCatalogueData;
  loading: boolean;
  error: boolean;
  loaded: boolean;
  /** The pro (by username) whose catalogue `data` holds — lets the UI refetch when the viewed pro changes. */
  username?: string;
}

const initialState: ServicesPricingState = {
  data: undefined,
  loading: false,
  error: false,
  loaded: false,
  username: undefined
};

export const servicesPricingSlice = createSlice({
  name: 'servicesPricing',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(getSellerServicePrices.pending, (state, action) => {
      state.loading = true;
      state.error = false;
      state.username = action.meta?.arg?.username;
    });
    builder.addCase(getSellerServicePrices.fulfilled, (state, action) => {
      state.data = action.payload;
      state.loading = false;
      state.error = false;
      state.loaded = true;
      state.username = action.meta?.arg?.username;
    });
    builder.addCase(getSellerServicePrices.rejected, state => {
      state.loading = false;
      state.error = true;
      state.loaded = true;
    });
  }
});

export const selectServicesPricing = (state: RootState): ServicesPricingState => state.servicesPricing;

export default servicesPricingSlice.reducer;
